import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Phone } from 'lucide-react'
import { useUserStore } from '../../stores'
import { useI18n } from '../../i18n'
import { isSmsRefused, smsRefusedSentence } from '../../utils/smsRefusal'
import { ResponsiveWrapper } from '../../components/layout'
import BottomActionBar from '../../components/BottomActionBar'
import orellaLogo from '../../assets/logos/orella_46A8F9_logo.png'

export default function ForgotPasswordScreen() {
  const navigate = useNavigate()
  const { requestPasswordReset, loading, error, clearError } = useUserStore()
  const { t } = useI18n()
  
  const [phone, setPhone] = useState('')
  const [localError, setLocalError] = useState(null)
  
  // Clear errors on mount
  useEffect(() => {
    clearError()
    setLocalError(null)
  }, [])
  
  const formatPhoneNumber = (value) => {
    const digits = value.replace(/\D/g, '')
    if (digits.length <= 3) return digits
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`
  }
  
  const handlePhoneChange = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 10)
    setPhone(formatPhoneNumber(digits))
  }
  
  const handleSubmit = async (e) => {
    e.preventDefault()
    setLocalError(null)
    clearError()
    
    const phoneDigits = phone.replace(/\D/g, '')
    
    if (!phoneDigits || phoneDigits.length < 10) {
      setLocalError(t('errors2.pleaseEnterPhone'))
      return
    }
    
    const result = await requestPasswordReset(phoneDigits)
    
    if (result.success) {
      if (!result.userId) {
        setLocalError(t('errors2.phoneRegistered'))
        return
      }
      
      // Navigate to verify screen with context
      navigate('/forgot-password/verify', { 
        state: { 
          userId: result.userId,
          resetToken: result.resetToken,
          smsSent: result.smsSent,
          maskedPhone: result.maskedPhone || `***-***-${phoneDigits.slice(-4)}`,
          phone: result.phone  // Phone for Twilio Verify
        },
        replace: true 
      })
    } else if (isSmsRefused(result)) {
      // PROD-807: a non-US/Canada number is never texted. Show the server's sentence, go
      // nowhere (no verify screen, nothing was sent); the e-mail reset link below stays as is.
      setLocalError(smsRefusedSentence(result) || t('errors2.failedToSendReset'))
    } else {
      setLocalError(result.error || t('errors2.failedToSendReset'))
    }
  }
  
  return (
    <ResponsiveWrapper className="bg-white" bottomInset={false}>
      <div className="screen-min-h bg-white px-6 md:px-10 lg:px-12 pt-8 flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <button 
            onClick={() => navigate('/signin')}
            className="p-2 -ml-2 text-gray-600 hover:text-gray-800"
          >
            <ArrowLeft size={24} />
          </button>
          <img 
            src={orellaLogo} 
            alt="Orella" 
            className="h-8 md:h-10 object-contain" 
          />
        </div>
        
        {/* Title */}
        <h1 className="text-2xl md:text-3xl font-bold text-orella-500 mb-2">
          {t('auth.forgotPasswordTitle')}
        </h1>
        <p className="text-gray-500 mb-8 md:text-lg">
          {t('auth.forgotPwdMessage')}
        </p>
        
        {/* Error Message */}
        {(localError || error) && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm">
            {localError || error}
          </div>
        )}
        
        {/* Form */}
        {/* PROD-1194: the form fills the column so Send sits pinned at the bottom of the screen. */}
        <form onSubmit={handleSubmit} className="flex-1 flex flex-col">
          <div>
            <label className="block text-sm text-gray-500 mb-1">
              {t('auth.phoneNumber', 'Phone Number')}
            </label>
            <div className="relative">
              <Phone className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="tel"
                value={phone}
                onChange={handlePhoneChange}
                className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:border-orella-500"
                placeholder={t('auth.phonePlaceholder')}
                autoComplete="tel"
                required
              />
            </div>
          </div>
          
          <BottomActionBar className="-mx-6 md:-mx-10 lg:-mx-12 [--bar-px:24px]">
            <button 
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-orella-500 text-white font-semibold rounded-full disabled:opacity-50 transition-opacity breathe-blue"
            >
              {loading ? t('common.sending') : t('auth.sendResetCode')}
            </button>
          </BottomActionBar>
        </form>
        
        {/* ★★★ THE WAY OUT FOR EVERYBODY THIS SCREEN CANNOT SERVE. Most patient
            accounts carry no telephone number at all, and a number that appears on
            more than one account deliberately sends nothing — those people reach
            this form and there is no next step for them. This is it. The SMS flow
            above is untouched. */}
        <p className="text-center text-gray-500 mt-4">
          <button
            onClick={() => navigate('/forgot-password/email')}
            className="text-orella-500 font-semibold underline"
          >
            {t('auth.resetByEmailInstead')}
          </button>
        </p>

        {/* Back to Sign In */}
        <p className="text-center text-gray-500 mt-4 bottom-actions [--ba-min:32px]">
          {t('auth.rememberPassword')}{' '}
          <button 
            onClick={() => navigate('/signin')} 
            className="text-orella-500 font-semibold"
          >
            {t('auth.signIn')}
          </button>
        </p>
      </div>
    </ResponsiveWrapper>
  )
}
